<script lang="ts">
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import Navbar from '../../components/Navbar.svelte';
	import AdminSidebar from '../../components/dashboard/AdminSidebar.svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';

	let isAdmin = false;
	let loading = $state(false);
	let currentPage = $state(1);
	let totalPages = $state(1);
	let totalCount = $state(0);
	let limit = 20;
	let error = $state('');
	let payments: any[] = $state([]);

	// Stats
	let totalRevenue = $state(0);
	let completedCount = $state(0);
	let pendingCount = $state(0);
	let failedCount = $state(0);

	onMount(async () => {
		const profile = get(appStore).profile;
		if (!profile || profile.userRole !== 'admin') {
			push('/');
			return;
		}
		isAdmin = true;
		await fetchPayments();
	});

	async function fetchPayments() {
		try {
			loading = true;
			error = '';
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getAllPayments({
				token,
				page: currentPage,
				limit
			});

			if (response.response.successFull) {
				payments = response.response.payments.map(p => ({
					id: p.id,
					invoiceNumber: p.invoiceNumber,
					userEmail: p.userEmail,
					amount: p.amount,
					currency: p.currency,
					status: p.status,
					paymentMethod: p.paymentMethod,
					createdAt: formatDate(p.createdAt)
				}));
				totalCount = response.response.totalCount;
				totalPages = response.response.totalPages;
				totalRevenue = response.response.totalRevenue;
				completedCount = response.response.completedCount;
				pendingCount = response.response.pendingCount;
				failedCount = response.response.failedCount;
			} else {
				error = response.response.reason || 'Failed to load payments';
			}
		} catch (e) {
			console.error('Error fetching payments:', e);
			error = 'Failed to load payments';
		} finally {
			loading = false;
		}
	}

	function goToPage(page: number) {
		currentPage = page;
		fetchPayments();
	}

	function formatDate(timestamp: any): string {
		if (!timestamp) return 'Unknown';
		const date = new Date(timestamp.seconds * 1000);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	function getStatusClass(status: string) {
		return status.toLowerCase();
	}
</script>

<Navbar />

<div class="page-wrapper">
	{#if isAdmin}
		<div class="page-layout">
			<AdminSidebar />
			<div class="page-main">
				<div class="page-header">
					<h1>Payments & Receipts</h1>
					<p>View and manage all payment transactions</p>
				</div>

				<div class="stats-row">
					<div class="stat-box">
						<div class="stat-label">Total Revenue</div>
						<div class="stat-value">${totalRevenue.toFixed(2)}</div>
					</div>
					<div class="stat-box">
						<div class="stat-label">Completed Payments</div>
						<div class="stat-value">{completedCount.toLocaleString()}</div>
					</div>
					<div class="stat-box">
						<div class="stat-label">Pending Payments</div>
						<div class="stat-value">{pendingCount.toLocaleString()}</div>
					</div>
					<div class="stat-box">
						<div class="stat-label">Failed Payments</div>
						<div class="stat-value">{failedCount.toLocaleString()}</div>
					</div>
				</div>

				<div class="content-card">
					<div class="card-header">
						<h2>All Transactions</h2>
						<span class="count">{totalCount} transactions</span>
					</div>

					{#if loading}
						<div class="loading">Loading payments...</div>
					{:else if error}
						<div class="error">{error}</div>
					{:else if payments.length === 0}
						<div class="empty-state">No payment transactions found</div>
					{:else}
						<div class="table-container">
							<table class="data-table">
								<thead>
									<tr>
										<th>Invoice #</th>
										<th>User</th>
										<th>Amount</th>
										<th>Payment Method</th>
										<th>Status</th>
										<th>Date</th>
									</tr>
								</thead>
								<tbody>
									{#each payments as payment}
										<tr>
											<td class="mono">{payment.invoiceNumber}</td>
											<td>{payment.userEmail}</td>
											<td class="amount">${payment.amount.toFixed(2)} {payment.currency}</td>
											<td>{payment.paymentMethod}</td>
											<td>
												<span class="status-badge {getStatusClass(payment.status)}">
													{payment.status}
												</span>
											</td>
											<td>{payment.createdAt}</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>

						{#if totalPages > 1}
							<div class="pagination">
								<button
									class="page-btn"
									disabled={currentPage === 1}
									onclick={() => goToPage(currentPage - 1)}
								>
									Previous
								</button>
								<span class="page-info">
									Page {currentPage} of {totalPages}
								</span>
								<button
									class="page-btn"
									disabled={currentPage === totalPages}
									onclick={() => goToPage(currentPage + 1)}
								>
									Next
								</button>
							</div>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.page-wrapper {
		min-height: 100vh;
		background: var(--bg-primary, #0d1117);
	}

	.page-layout {
		display: flex;
		min-height: calc(100vh - 60px);
	}

	.page-main {
		flex: 1;
		padding: 2rem;
		overflow-y: auto;
	}

	.page-header {
		margin-bottom: 2rem;
	}

	.page-header h1 {
		font-size: 2rem;
		font-weight: 600;
		margin: 0 0 0.5rem 0;
		color: var(--text-primary, #e6edf3);
	}

	.page-header p {
		color: var(--text-secondary, #8b949e);
		margin: 0;
	}

	.stats-row {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 1rem;
		margin-bottom: 2rem;
	}

	.stat-box {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.stat-label {
		font-size: 0.875rem;
		color: var(--text-secondary, #8b949e);
		margin-bottom: 0.5rem;
	}

	.stat-value {
		font-size: 1.75rem;
		font-weight: 600;
		color: var(--text-primary, #e6edf3);
		margin-bottom: 0.5rem;
	}

	.stat-change {
		font-size: 0.8125rem;
		color: var(--text-secondary, #8b949e);
	}

	.stat-change.positive {
		color: #7eb845;
	}

	.stat-change.negative {
		color: #f85149;
	}

	.content-card {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.card-header h2 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0;
		color: var(--text-primary, #e6edf3);
	}

	.count {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.table-container {
		overflow-x: auto;
	}

	.data-table {
		width: 100%;
		border-collapse: collapse;
	}

	.data-table thead th {
		text-align: left;
		padding: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text-secondary, #8b949e);
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.data-table tbody td {
		padding: 0.75rem;
		border-bottom: 1px solid var(--border-secondary, #21262d);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
	}

	.data-table tbody tr:hover {
		background: var(--bg-tertiary, #1c2128);
	}

	.mono {
		font-family: 'Courier New', monospace;
		font-size: 0.8125rem;
	}

	.amount {
		font-weight: 600;
		font-variant-numeric: tabular-nums;
	}

	.status-badge {
		padding: 0.25rem 0.75rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
		text-transform: capitalize;
	}

	.status-badge.completed {
		background: rgba(126, 184, 69, 0.1);
		color: #7eb845;
	}

	.status-badge.pending {
		background: rgba(255, 191, 0, 0.1);
		color: #ffbf00;
	}

	.status-badge.failed {
		background: rgba(248, 81, 73, 0.1);
		color: #f85149;
	}

	.action-btn {
		padding: 0.375rem 0.75rem;
		border-radius: 4px;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		font-size: 0.8125rem;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.action-btn:hover {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.loading,
	.error,
	.empty-state {
		padding: 2rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.error {
		color: #f85149;
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 1rem;
		margin-top: 1.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--border-primary, #30363d);
	}

	.page-btn {
		padding: 0.5rem 1rem;
		border-radius: 6px;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.page-btn:hover:not(:disabled) {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
	}

	.page-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.page-info {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}
</style>
